import { PlayerDetails } from "@/lib/types";
import { BaseFormLayout } from "@/pages/shared/BaseFormLayout";
import { useTranslation } from "react-i18next";
import { PlayerKioskInstructions } from "./PlayerKioskInstructions";
import settings from "@/settings.json";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CopyIcon, ExternalLinkIcon, MonitorIcon, SmartphoneIcon, CheckCircleIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { getPlayerDetails } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";
import { showSuccess } from "@/lib/toast";

interface Props {
  playerDetails: PlayerDetails;
  onClickSkip: () => void;
}

const WeblinkDomain = settings.WeblinkDomain;

export const PlayerSetupPage = ({ playerDetails, onClickSkip }: Props) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const [setupMethod, setSetupMethod] = useState<"web" | "kiosk" | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const code = playerDetails.code;

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const handleOpenWeblink = () => {
    window.open(`${WeblinkDomain}/ds`, "_blank");
  };

  const handleCompletion = async () => {
    setIsCompleting(true);
    try {
      const response = await getPlayerDetails(String(playerDetails.id), String(activeFirm?.firmGuid));
      if (response.data.deviceType) {
        setIsCompleting(false);
        showSuccess(t("player.setupCompleted"));
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <BaseFormLayout
      title={
        <>
          <div className="mb-2">{t("player.setupTitle", { name: playerDetails.name })}</div>
          <div className="text-muted-foreground text-sm font-medium">{t("player.setupRequired")}</div>
        </>
      }
    >
      {!setupMethod ? (
        <div className="space-y-6">
          <h2 className="text-2xl font-semibold text-center mb-8">{t("player.chooseSetupMethod")}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card
              className="cursor-pointer hover:border-primary hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 bg-gradient-to-br from-white to-blue-50/30 dark:from-gray-900 dark:to-blue-900/30"
              onClick={() => setSetupMethod("web")}
            >
              <CardHeader className="space-y-1">
                <CardTitle className="text-xl flex items-center gap-2">
                  <SmartphoneIcon className="h-5 w-5 text-blue-500" />
                  {t("player.webSetup")}
                </CardTitle>
                <CardDescription>{t("player.webSetupDescription")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-32 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/50 dark:to-indigo-900/50 rounded-md">
                  <SmartphoneIcon className="h-16 w-16 text-blue-400" />
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 dark:from-blue-600 dark:to-indigo-600 dark:hover:from-blue-500 dark:hover:to-indigo-500 transition-all duration-300">
                  {t("common.select")}
                </Button>
              </CardFooter>
            </Card>

            <Card
              className="cursor-pointer hover:border-primary hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 bg-gradient-to-br from-white to-purple-50/30 dark:from-gray-900 dark:to-purple-900/30"
              onClick={() => setSetupMethod("kiosk")}
            >
              <CardHeader className="space-y-1">
                <CardTitle className="text-xl flex items-center gap-2">
                  <MonitorIcon className="h-5 w-5 text-purple-500" />
                  {t("player.kioskSetup")}
                </CardTitle>
                <CardDescription>{t("player.kioskSetupDescription")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-32 flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/50 dark:to-pink-900/50 rounded-md">
                  <MonitorIcon className="h-16 w-16 text-purple-400" />
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 dark:from-purple-600 dark:to-pink-600 dark:hover:from-purple-500 dark:hover:to-pink-500 transition-all duration-300">
                  {t("common.select")}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">
              {setupMethod === "web" ? t("player.webSetupInstructions") : t("player.kioskSetupInstructions")}
            </h2>
            <Button variant="outline" onClick={() => setSetupMethod(null)}>
              {t("common.back")}
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{setupMethod === "web" ? t("player.webSetupSteps") : t("player.kioskSetupSteps")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {setupMethod === "web" ? (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <span className="flex h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-primary-foreground items-center justify-center text-sm">
                        1
                      </span>
                      {t("player.copyPlayerCode")}
                    </h3>
                    <div className="flex items-center gap-2">
                      <Input value={code} readOnly className="font-mono text-base" />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyCode}
                        className={cn(
                          "transition-all duration-300",
                          codeCopied
                            ? "text-green-500 border-green-500 bg-green-50"
                            : "hover:bg-blue-50 hover:border-blue-500 hover:text-blue-500"
                        )}
                      >
                        {codeCopied ? <CheckCircleIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <span className="flex h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-primary-foreground items-center justify-center text-sm">
                        2
                      </span>
                      {t("player.openPlayerPortal")}
                    </h3>
                    <div className="flex items-center gap-2">
                      <Input value={`${WeblinkDomain}/ds`} readOnly />
                      <Button variant="outline" size="icon" onClick={handleOpenWeblink}>
                        <ExternalLinkIcon className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">{t("player.openInNewTab")}</p>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <span className="flex h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-primary-foreground items-center justify-center text-sm">
                        3
                      </span>
                      {t("player.pasteAndRegister")}
                    </h3>
                    <p className="text-sm">{t("player.pasteCodeInstructions")}</p>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <span className="flex h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-primary-foreground items-center justify-center text-sm">
                        4
                      </span>
                      {t("player.completeSetup")}
                    </h3>
                    <p className="text-sm">{t("player.clickWhenComplete")}</p>
                    <Button
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 dark:from-green-600 dark:to-emerald-600 dark:hover:from-green-500 dark:hover:to-emerald-500 transition-all duration-300"
                      onClick={handleCompletion}
                      disabled={isCompleting}
                    >
                      {isCompleting ? t("common.loading") : t("player.iCompletedCode")}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <span className="flex h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-primary-foreground items-center justify-center text-sm">
                        1
                      </span>
                      {t("player.setupKioskMode")}
                    </h3>
                    <PlayerKioskInstructions />
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <span className="flex h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-primary-foreground items-center justify-center text-sm">
                        2
                      </span>
                      {t("player.copyPlayerCode")}
                    </h3>
                    <div className="flex items-center gap-2">
                      <Input
                        value={code}
                        readOnly
                        className="font-mono text-base bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-900 dark:to-blue-900"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={handleCopyCode}
                        className={cn(
                          "transition-all duration-300",
                          codeCopied
                            ? "text-green-500 border-green-500 bg-green-50"
                            : "hover:bg-blue-50 hover:border-blue-500 hover:text-blue-500"
                        )}
                      >
                        {codeCopied ? <CheckCircleIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <span className="flex h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-primary-foreground items-center justify-center text-sm">
                        3
                      </span>
                      {t("player.pasteAndRegister")}
                    </h3>
                    <p className="text-sm">{t("player.pasteCodeInstructions")}</p>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium flex items-center gap-2">
                      <span className="flex h-6 w-6 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 text-primary-foreground items-center justify-center text-sm">
                        4
                      </span>
                      {t("player.completeSetup")}
                    </h3>
                    <p className="text-sm">{t("player.clickWhenComplete")}</p>
                    <Button
                      className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 dark:from-green-600 dark:to-emerald-600 dark:hover:from-green-500 dark:hover:to-emerald-500 transition-all duration-300"
                      onClick={handleCompletion}
                      disabled={isCompleting}
                    >
                      {isCompleting ? t("common.loading") : t("player.iCompletedCode")}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline" onClick={() => setSetupMethod(null)}>
                {t("common.back")}
              </Button>
              <Button onClick={handleOpenWeblink} variant="outline">
                {setupMethod === "web" ? t("player.openPlayerPortal") : t("player.openKioskPortal")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      <div className="flex justify-center mt-8">
        <Button variant="outline" onClick={onClickSkip}>
          {t("Skip")}
        </Button>
      </div>
    </BaseFormLayout>
  );
};
